import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileClock, Users, BarChart3 } from "lucide-react";
import { SlipReportDialog } from "@/components/SlipReportDialog";
import { toast } from "sonner";

const REMIND_MS = 3 * 3600000;
const LATE_MS = 24 * 3600000;
const isLate = (it) => Date.now() - new Date(it.completed_at).getTime() >= LATE_MS;
// Every 3h a deferred slip keeps waiting, nudge the processor once (per browser).
const remindDeferred = (items, onOpen) => {
  const now = Date.now();
  items.filter(it => it.slip_check?.status === "deferred").forEach(it => {
    const since = new Date(it.slip_check.deferred_at || it.completed_at).getTime();
    const key = `slip-remind-${it.invoice_key}`;
    const last = Number(localStorage.getItem(key) || 0);
    if (now - since >= REMIND_MS && now - last >= REMIND_MS) {
      localStorage.setItem(key, String(now));
      toast.warning(`Packing slip for ${it.invoice_key} is still pending (${ago(it.completed_at)} since completion)`, {
        duration: 15000, action: { label: "Upload now", onClick: () => onOpen(it.invoice_key, "deferred") } });
    }
  });
};

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
};
const tone = (iso) => {
  const h = (Date.now() - new Date(iso).getTime()) / 3600000;
  return h >= 24 ? "bg-rose-600 text-white" : h >= 4 ? "bg-amber-500 text-white" : "bg-emerald-600 text-white";
};

// Completed invoices (by this user) still waiting for their packing slip.
export const PendingSlipsButton = ({ onOpen, refreshKey, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [everyone, setEveryone] = useState(false);
  const [report, setReport] = useState(false);
  const [open, setOpen] = useState(false);
  const lateWarned = useRef(false);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/packing-list/slip-check/pending`, { headers: getAuthHeader(), params: { all: true, everyone } });
      const list = r.data || [];
      setItems(list);
      remindDeferred(everyone ? [] : list, (...a) => openRef.current(...a));
    } catch { setItems([]); }
  }, [everyone]);
  useEffect(() => { load(); }, [load, refreshKey]);
  // Owner alert: once per page load, list every slip waiting over 24h (all users).
  useEffect(() => {
    if (!isAdmin || lateWarned.current) return;
    lateWarned.current = true;
    axios.get(`${API}/packing-list/slip-check/pending`, { headers: getAuthHeader(), params: { all: true, everyone: true } })
      .then(r => {
        const late = (r.data || []).filter(isLate);
        if (!late.length) return;
        // Closed with ✕ → stay quiet until the count changes or 4 hours pass.
        const snooze = JSON.parse(localStorage.getItem("slip-late-snooze") || "{}");
        if (snooze.n === late.length && Date.now() - (snooze.at || 0) < 4 * 3600000) return;
        toast.error(`${late.length} packing slip(s) waiting over 24 hours`, {
          duration: 30000, id: "slip-late-alert",
          cancel: { label: "✕", onClick: () => localStorage.setItem("slip-late-snooze", JSON.stringify({ n: late.length, at: Date.now() })) },
          description: <div style={{ whiteSpace: "pre-line" }}>{late.slice(0, 6).map(it => `${it.invoice_key} — ${it.slip_check?.by_name || it.slip_check?.by_email} — ${ago(it.completed_at)}`).join("\n")
            + (late.length > 6 ? `\n+${late.length - 6} more` : "")}</div>,
          action: { label: "Open list", onClick: () => { setEveryone(true); setOpen(true); } },
        });
      }).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("order-register:invoice-changed", h);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("order-register:invoice-changed", h); clearInterval(t); };
  }, [load]);
  if (!items.length && !isAdmin) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={`h-8 text-[11px] font-semibold ${items.some(isLate) ? "bg-rose-600 text-white border-rose-600 hover:bg-rose-700 animate-pulse" : "border-rose-400 text-rose-700 hover:bg-rose-50"}`}
          data-testid="or-pending-slips-btn" title="Completed invoices still waiting for their packing slip">
          <FileClock className="w-3 h-3 mr-1" /> Pending slips
          <span className={`ml-1 rounded-full px-1.5 text-[10px] leading-4 ${items.some(isLate) ? "bg-white text-rose-700" : "bg-rose-600 text-white"}`} data-testid="or-pending-slips-count">{items.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-2" align="start" data-testid="or-pending-slips-list">
        <div className="flex items-center justify-between px-1 pb-1">
          <p className="text-xs font-semibold text-slate-700">{everyone ? "All users — pending packing slips" : "Packing slips to upload"}</p>
          {isAdmin && (
            <button type="button" onClick={() => setReport(true)} data-testid="or-slip-report-btn"
              className="ml-auto mr-1 inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50">
              <BarChart3 className="w-3 h-3" /> Slip report
            </button>)}
          {isAdmin && (
            <button type="button" onClick={() => setEveryone(v => !v)} data-testid="or-pending-slips-everyone"
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${everyone ? "bg-violet-600 text-white border-violet-600" : "border-violet-300 text-violet-700"}`}>
              <Users className="w-3 h-3" /> All users
            </button>)}
        </div>
        {!items.length && <p className="px-2 py-3 text-xs text-slate-500">Nothing pending.</p>}
        {items.map(it => (
          <button key={it.invoice_key} type="button" onClick={() => onOpen(it.invoice_key, it.slip_check?.status)}
            className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100"
            data-testid={`or-pending-slip-${it.invoice_key}`}>
            <span className="font-mono font-semibold text-violet-700">{it.invoice_key}</span>
            {everyone && <span className="text-[10px] text-slate-600 shrink-0" data-testid={`or-pending-slip-user-${it.invoice_key}`}>{it.slip_check?.by_name || it.slip_check?.by_email}</span>}
            <span className="truncate text-slate-500 flex-1" title={it.slip_check?.deferred_reason || ""}>
              {it.slip_check?.status === "deferred" ? `Later: ${it.slip_check?.deferred_reason || ""}` : "Waiting"}
            </span>
            <span className={`rounded-full px-1.5 text-[10px] font-bold leading-4 ${tone(it.completed_at)}`}
                  title="Time since the invoice was completed" data-testid={`or-pending-slip-age-${it.invoice_key}`}>
              {ago(it.completed_at)}
            </span>
          </button>
        ))}
      </PopoverContent>
      {isAdmin && <SlipReportDialog open={report} onClose={() => setReport(false)} />}
    </Popover>
  );
};
